import { AdjustmentsHorizontalIcon, FolderIcon } from "@heroicons/react/24/outline";
import { pick, transform, uniq } from "lodash-es";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { favoriteFiltersListName, ListFieldsType, myDocumentsListName, siteUrl } from "src/api/config";
import { elasticSearchByTitle, elasticSearchByText } from "src/api/elasticsearch";
import { useFiltersContext } from "src/contexts/FilterContext";
import { useFoldersContext } from "src/contexts/FolderContext";
import { useAppDispatch, useAppSelector } from "src/hooks/redux";
import { modifiedMyDocumentsList } from "src/modules/acadia/my_documents/selectors";
import { addItem, deleteItem, getItems, updateItem } from "src/modules/spItems/actions";
import { getItemsRequest } from "src/modules/spItems/api";
import {
  modifiedItemsState,
  selectAddStatus,
  selectDeleteStatus,
  selectItems,
  selectUpdateStatus,
} from "src/modules/spItems/selectors";
import { selectCurrentUser } from "src/modules/users/selector";
import { LayoutWithAsideAndContent } from "src/pages/LayoutWithAsideAndContent";
import FavoriteFiltersModal from "src/pages/Reports/WorkPlaceReport/components/FiltersModal";
import { MyDocumentsType, UserListItem } from "src/types/main";
import { getLocalItem, setLocalItem } from "src/utils/localStorage";
import { buildDocumentSearchFilter, isCodeIndexQuery } from "src/utils/searchFilter.utils";
import { searchAndHighlight } from "src/utils/searchHighlight";
import { twMerge } from "tailwind-merge";
import TableWithFilters from "../../../components/tables/TableWithFilters";
import { SearchUI } from "../../../components/ui/Search";
import AddCard from "../../../modules/acadia/addCard";
import { FlexCol, FlexRow } from "../../../utils/flexLayouts";
import CustomCheckbox from "./components/Tests/components/Ui/CustomCheckbox";
import DocumentFilters, { DocumentPageFilterBreadcrumbLayout } from "./components/Filters/DocumentFilters";
import Folders, { DocumentPageFolderBreadcrumbLayout } from "./components/Folders";
import DocFavoriteFiltersManager, { isDocFiltersEmpty } from "./components/favorites/DocFavoriteFiltersManager";
import { keyMap } from "./data/keyMap";
import { FilterType, Keys, KeysList, RestKeys, UserKeys } from "./type";
import { addUsers } from "src/modules/users/actions";
import TGButton from "src/components/ui/TGButton";
import DialogComponent from "src/components/DialogComponent";
import InputFilterSet from "src/components/InputFilterSet";
import { SaveIcon } from "src/pages/Reports/shared/icons";

const searchPaths = [
  "code.code",
  "code.name",
  // "aboutDocument.collection",
  // "aboutDocument.department",
  // "aboutDocument.sector",
  // "aboutDocument.process",
  // "aboutDocument.type",
  // "responsibleForRevision.name",
];

const EMPTY_SEARCH_META = {
  foundBy: "title" as const,
  highlightedText: "",
  elasticsearchId: "",
  elasticsearchScore: 0,
  DocumentFileId: null,
};

function mergeSearchResults(
  spResults: MyDocumentsType[],
  esTitleResults: MyDocumentsType[],
  esTextResults: MyDocumentsType[],
): MyDocumentsType[] {
  const esMap = new Map<number, MyDocumentsType>();

  for (const doc of esTitleResults) {
    esMap.set(doc.id, doc);
  }
  for (const doc of esTextResults) {
    const existing = esMap.get(doc.id);
    if (existing) {
      esMap.set(doc.id, { ...doc, searchMeta: { ...doc.searchMeta!, foundBy: "both" } });
    } else {
      esMap.set(doc.id, doc);
    }
  }

  const spIds = new Set(spResults.map((d) => d.id));

  const mergedSp = spResults.map((d) => {
    const esMatch = esMap.get(d.id);
    if (esMatch?.searchMeta) return { ...d, searchMeta: esMatch.searchMeta };
    return { ...d, searchMeta: EMPTY_SEARCH_META };
  });

  const esOnly = [...esMap.values()].filter((d) => !spIds.has(d.id));
  const esOnlyTitle = esOnly.filter((d) => d.searchMeta?.foundBy !== "content");
  const esOnlyContent = esOnly.filter((d) => d.searchMeta?.foundBy === "content");

  return [...mergedSp, ...esOnlyTitle, ...esOnlyContent];
}

const MyDocumentPage = () => {
  const { folders, fromFilterToFolders, clearFolders, isFoldersEmpty } = useFoldersContext();
  const { filters, fromFolderToFilters, statusFilters } = useFiltersContext();
  const [activeFilterType, setActiveFilterType] = useState<FilterType>(getLocalItem("filter_active") || "folder");

  // Guard: auto-apply folders only once per page lifecycle (prevents re-applying after user
  // manually clears a folder selection or navigates away and comes back).
  const foldersDefaultApplied = useRef(false);

  // When DocFavoriteFiltersManager applies Priority 2 (user's collection + department) to
  // FilterContext, this effect catches the filter change and converts it into folder state.
  // Also handles the simpler case where filters were already saved in localStorage.
  // Runs only when filters change, and only once (ref guard).
  useEffect(() => {
    if (foldersDefaultApplied.current) return;   // already applied — don't touch user's selection
    if (activeFilterType !== "folder") return;    // only relevant in folder mode
    if (isFoldersEmpty()) return;                 // folders already have a selection
    if (statusFilters) return;                    // filters are in "override" mode
    if (isDocFiltersEmpty(filters)) return;       // filters not ready yet — wait for next update

    foldersDefaultApplied.current = true;
    fromFilterToFolders(filters);
  }, [JSON.stringify(filters)]); // react whenever filters are updated (e.g. by Priority 2)

  function toggleFilter(type: FilterType) {
    if (type === "filter") {
      if (!statusFilters) {
        fromFolderToFilters(folders);
      }
    }

    if (type === "folder") {
      if (!statusFilters) {
        fromFilterToFolders(filters);
      } else {
        clearFolders();
      }
    }

    setLocalItem("filter_active", type);
    setActiveFilterType(type);
  }

  if (!getLocalItem("filter_active")) {
    setLocalItem("filter_active", "folder");
  }

  return (
    <LayoutWithAsideAndContent
      aside={
        <>
          <FlexRow className="border-b flex-none divide-x cursor-pointer items-center h-[76px]">
            <FlexCol
              onClick={() => toggleFilter("folder")}
              className={twMerge(
                activeFilterType === "folder" ? "text-darkblue" : "",
                "items-center justify-center w-1/2 h-full",
              )}
            >
              <FlexRow className="gap-x-2 items-center">
                <FlexCol>
                  <FolderIcon className="w-4 h-4" />
                </FlexCol>
                <FlexCol>Папки</FlexCol>
              </FlexRow>
            </FlexCol>
            <FlexCol
              onClick={() => toggleFilter("filter")}
              className={twMerge(
                activeFilterType === "filter" ? "text-darkblue" : "",
                "items-center justify-center w-1/2 h-full",
              )}
            >
              <FlexRow className="gap-x-2 items-center">
                <FlexCol>
                  <AdjustmentsHorizontalIcon className="w-4 h-4" />
                </FlexCol>
                <FlexCol>Фильтры</FlexCol>
              </FlexRow>
            </FlexCol>
          </FlexRow>
          <FlexRow className="overflow-y-auto scrollbar scrollbar-w-2 scrollbar-thumb-rounded-md scrollbar-thumb-[#D1D5DB]">
            {activeFilterType === "folder" ? <Folders /> : <DocumentFilters />}
          </FlexRow>
        </>
      }
      content={<ContentArea activeFilterType={activeFilterType} />}
    />
  );
};

function ContentArea({ activeFilterType }: { activeFilterType: "folder" | "filter" }) {
  const [loading, setLoading] = useState(false);
  const [nextPage, setNextPage] = useState<number | null>(null);

  const dispatch = useAppDispatch();

  const { filters } = useFiltersContext();
  const { folders, isFoldersEmpty } = useFoldersContext();
  const user = useAppSelector((state) => selectCurrentUser(state));
  const favoriteFiltersData = useAppSelector((state) =>
    selectItems(state, favoriteFiltersListName),
  ) as ListFieldsType[typeof favoriteFiltersListName][];
  const favoriteFiltersUpdatedStatus = useAppSelector((state) => selectUpdateStatus(state, favoriteFiltersListName));
  const favoriteFiltersDeletedStatus = useAppSelector((state) => selectDeleteStatus(state, favoriteFiltersListName));
  const favoriteFiltersAddedStatus = useAppSelector((state) => selectAddStatus(state, favoriteFiltersListName));

  const [documents, setDocuments] = useState<MyDocumentsType[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchByPartialMatch, setSearchByPartialMatch] = useState(false);
  const [searchByContent, setSearchByContent] = useState(false);
  const [page, setPage] = useState(1);

  const [esTitleFrom, setEsTitleFrom] = useState(0);
  const [esTextFrom, setEsTextFrom] = useState(0);
  const [hasMoreEsTitle, setHasMoreEsTitle] = useState(false);
  const [hasMoreEsText, setHasMoreEsText] = useState(false);

  // Favorites state
  const [isFavorite, setIsFavorite] = useState(false);
  const [currentFavoriteId, setCurrentFavoriteId] = useState<string | null>(null);
  const [bundlesModalOpened, setBundlesModalOpened] = useState(false);
  const [favoriteFilterModalOpened, setFavoriteFilterModalOpened] = useState(false);
  const [filtersReady, setFiltersReady] = useState(() => !isDocFiltersEmpty(filters));
  const [filterTitle, setIsFilterSetTitle] = useState("");
  const [isSaved, setIsSaved] = useState(false);

  const getSetTitle = useCallback((title: string) => {
    setIsFilterSetTitle(title);
  }, []) as (title: string) => void;

  function openUpdateFilterDialog() {
    setIsSaved(true);
    if (currentFavoriteId) {
      setIsFilterSetTitle(
        favoriteFiltersData.find((item) => item.ID.toString() === currentFavoriteId?.toString())?.Title || "",
      );
    }
  }

  function updateSetFilter() {
    if (currentFavoriteId) {
      dispatch(updateItem({ listName: favoriteFiltersListName, body: { ID: currentFavoriteId, Title: filterTitle } }));
    }

    closeDialog();
  }

  function closeDialog() {
    setIsSaved(false);
  }
  function cb() {
    setPage((prev) => prev + 1);
  }

  function handleSearch(value: string) {
    setSearchQuery(value);
  }

  const handleToggleSearchByPartialMatch = () => {
    setSearchByPartialMatch((previousValue) => !previousValue);
  };

  const handleToggleSearchByContent = () => {
    setSearchByContent((prev) => !prev);
  };

  const saveFilter = ({ name, isDefault }: { name: string; isDefault: boolean }) => {
    if (isDefault) {
      favoriteFiltersData
        .filter((item) => item.isDefault && item.Type === "doc")
        .forEach((item) => {
          dispatch(updateItem({ listName: favoriteFiltersListName, body: { ID: item.ID, isDefault: false } }));
        });
    }
    dispatch(
      addItem({
        listName: favoriteFiltersListName,
        body: {
          Title: name,
          DocumentsFilter: JSON.stringify(filters),
          Type: "doc",
          isDefault: isDefault,
        },
      }),
    );
    setFavoriteFilterModalOpened(false);
  };

  const deleteCurrentFilter = () => {
    if (currentFavoriteId) {
      dispatch(deleteItem({ listName: favoriteFiltersListName, ID: currentFavoriteId }));
      setIsFavorite(false);
      setCurrentFavoriteId(null);
    }
  };

  // Reload favorites list after any mutation
  useEffect(() => {
    if (
      (favoriteFiltersUpdatedStatus === "SUCCESS" ||
        favoriteFiltersDeletedStatus === "SUCCESS" ||
        favoriteFiltersAddedStatus === "SUCCESS") &&
      user.id
    ) {
      dispatch(getItems({ listName: favoriteFiltersListName, filters: { Author: user.id, Type: "doc" } }));
    }
  }, [favoriteFiltersUpdatedStatus, favoriteFiltersDeletedStatus, favoriteFiltersAddedStatus]);

  const computedBreadcrumbs = useMemo(() => {
    if (activeFilterType === "folder") {
      return <DocumentPageFolderBreadcrumbLayout />;
    }
    return (
      <DocumentPageFilterBreadcrumbLayout
        isFavorite={isFavorite}
        currentFavoriteId={currentFavoriteId}
        bundlesModalOpened={bundlesModalOpened}
        setBundlesModalOpened={setBundlesModalOpened}
        onSaveFilter={() => setFavoriteFilterModalOpened(true)}
        onDeleteFilter={deleteCurrentFilter}
        onUpdateFilter={() => openUpdateFilterDialog()}
      />
    );
  }, [activeFilterType, isFavorite, currentFavoriteId, bundlesModalOpened]);

  useLayoutEffect(() => {
    if (page !== 1) {
      setPage(1);
    }
    setLoading(true);
    setEsTitleFrom(0);
    setEsTextFrom(0);
    setHasMoreEsTitle(false);
    setHasMoreEsText(false);
  }, [JSON.stringify(filters), JSON.stringify(folders)]);

  useLayoutEffect(() => {
    setPage(1);
    setLoading(true);
    setEsTitleFrom(0);
    setEsTextFrom(0);
    setHasMoreEsTitle(false);
    setHasMoreEsText(false);
  }, [searchQuery, searchByPartialMatch, searchByContent]);

  const filterElasticResults = (results: MyDocumentsType[]): MyDocumentsType[] => {
    return results.filter((doc) => {
      if (activeFilterType === "folder") {
        if (folders.collections && doc.aboutDocument.collection !== folders.collections.title) return false;
        if (folders.departments && doc.aboutDocument.department !== folders.departments.title) return false;
        if (folders.sectors && doc.aboutDocument.sector !== folders.sectors.title) return false;
        if (folders.processes && doc.aboutDocument.process !== folders.processes.title) return false;
        if (folders.types && doc.aboutDocument.type !== folders.types.title) return false;
      } else {
        if (
          filters.collections.length > 0 &&
          !filters.collections.some((f) => f.title === doc.aboutDocument.collection)
        )
          return false;
        if (
          filters.departments.length > 0 &&
          !filters.departments.some((f) => f.title === doc.aboutDocument.department)
        )
          return false;
        if (filters.sectors.length > 0 && !filters.sectors.some((f) => f.title === doc.aboutDocument.sector))
          return false;
        if (filters.processes.length > 0 && !filters.processes.some((f) => f.title === doc.aboutDocument.process))
          return false;
        if (filters.types.length > 0 && !filters.types.some((f) => f.title === doc.aboutDocument.type)) return false;
      }

      if (filters.critical.length > 0 && filters.critical.length < 2) {
        const isCritical = Boolean(filters.critical[0].id);
        if (doc.critical !== isCritical) return false;
      }

      if (filters.responsibleForRevision.length > 0) {
        const emails = filters.responsibleForRevision.map((r) => r.email.toLowerCase());
        if (!emails.includes(doc.responsibleForRevision.email.toLowerCase())) return false;
      }

      return true;
    });
  };

  const fetchElasticResultsWithFallback = useCallback(
    async (
      searchFn: (query: string, from?: number) => Promise<MyDocumentsType[]>,
      startFrom: number,
      rowLimit: number = 100,
      minFilteredResults: number = 50,
      onFilteredChunk?: (chunk: MyDocumentsType[]) => void,
    ): Promise<{ results: MyDocumentsType[]; hasMore: boolean; lastFrom: number }> => {
      const maxRequests = 20;
      let currentFrom = startFrom;
      let allResults: MyDocumentsType[] = [];
      let hasMore = true;
      let lastFrom = startFrom;
      let requestsCount = 0;

      while (hasMore && allResults.length < minFilteredResults && requestsCount < maxRequests) {
        const chunk = await searchFn(searchQuery, currentFrom);
        requestsCount += 1;
        lastFrom = currentFrom;

        if (!chunk.length) {
          hasMore = false;
          continue;
        }

        const filtered = filterElasticResults(chunk);
        if (filtered.length > 0) {
          allResults = [...allResults, ...filtered];
          onFilteredChunk?.(filtered);
        }
        currentFrom += rowLimit;
      }

      if (requestsCount >= maxRequests && allResults.length < minFilteredResults) {
        hasMore = false;
      }

      return { results: allResults, hasMore, lastFrom };
    },
    [searchQuery, filterElasticResults],
  );

  function modified(data: ListFieldsType[typeof myDocumentsListName][]) {
    const myDocuments = modifiedMyDocumentsList(data);
    return searchAndHighlight(myDocuments, searchQuery || "", searchPaths);
  }

  useEffect(() => {
    const hasSearch = searchQuery.length > 0;

    if (!hasSearch) {
      if (activeFilterType === "filter") {
        if (!filtersReady || isDocFiltersEmpty(filters)) {
          setDocuments([]);
          setLoading(false);
          return;
        }
      } else {
        if (!isFoldersEmpty()) {
          setDocuments([]);
          setLoading(false);
          return;
        }
      }
    }

    let cancelled = false;

    async function fetchData() {
      setLoading(true);

      if (page === 1) {
        setDocuments([]);
        setNextPage(null);
      }

      try {
        let baseFilters = pick(filters, ["collections", "departments", "sectors", "processes", "types"]);
        let restFilters = pick(filters, [
          "critical",
          "lines",
          "sap",
          "typeEquipment",
          "model",
          "manufacturer",
          "titleEquipment",
          "workstations",
        ]);
        let userFilters = pick(filters, ["created", "responsibleForRevision"]);
        let dateFilters = pick(filters, ["dateForRevision"]);

        const configureBaseFilters: Record<Keys, number[]> =
          activeFilterType === "filter"
            ? transform(baseFilters, (result: any, value: any[], key: string) => {
                let newKey = keyMap[key as KeysList];
                let newValue = value.map((v: { id: any }) => v.id);

                if (newValue.length > 0) {
                  (result as any)[newKey] = newValue;
                }
              })
            : transform(folders, (result: any, value: any, key: string) => {
                let newKey = keyMap[key as KeysList];

                if (value) {
                  (result as any)[newKey] = value.id;
                }
              });

        const userFiltersData = Object.keys(userFilters).reduce(
          (acc, key) => {
            const newKey = keyMap[key as UserKeys];

            if (userFilters[key as UserKeys].length > 0) {
              const ids = userFilters[key as UserKeys].map((r: UserListItem) => r.email.toLowerCase());
              acc[newKey] = ids;
            }

            return acc;
          },
          {} as Record<(typeof keyMap)[UserKeys], string[]>,
        );

        const allFilters = Object.keys(restFilters).reduce(
          (acc, key) => {
            const newValue = restFilters[key as RestKeys];
            const newKey = keyMap[key as RestKeys];

            if (key === "critical") {
              if (newValue.length > 0 && newValue.length < 2) {
                acc[newKey] = Boolean(newValue[0].id);
              }
            } else {
              if (newValue.length > 0) {
                const arrayIds = newValue.map((r: { id: any }) => r.id);
                if (acc[newKey]) {
                  acc[newKey] = uniq([...acc[newKey], ...arrayIds]);
                } else {
                  acc[newKey] = newValue.map((r: { id: any }) => r.id);
                }
              }
            }

            return acc;
          },
          // Если запущена локалка в таблице будут выводится все документы
          { ...configureBaseFilters, ...userFiltersData, Status: [8, 3] } as Record<string, any>, // 8 - опубликован, 3 - на пересмотр
        );

        if (
          dateFilters.dateForRevision.required ||
          dateFilters.dateForRevision.date.start ||
          dateFilters.dateForRevision.date.end
        ) {
          let dateFiltersData: { start: Date | null; end: Date | null }[] = [];
          const newKey = keyMap["dateForRevision"];

          if (dateFilters.dateForRevision.required) {
            allFilters["Status"] = [3]; // на пересмотр

            // dateFiltersData.push({ start: new Date(), end: moment(new Date()).add(30, "day").toDate() });
          }

          if (dateFilters.dateForRevision.date.start || dateFilters.dateForRevision.date.end) {
            dateFiltersData.push({
              start:
                typeof dateFilters.dateForRevision.date.start === "string"
                  ? new Date(dateFilters.dateForRevision.date.start)
                  : dateFilters.dateForRevision.date.start,
              end:
                typeof dateFilters.dateForRevision.date.end === "string"
                  ? new Date(dateFilters.dateForRevision.date.end)
                  : dateFilters.dateForRevision.date.end,
            });

            allFilters[newKey] = dateFiltersData;
          }
        }

        if (searchQuery.length > 0) {
          const usePartialMatchSearch = searchByPartialMatch || searchByContent;
          Object.assign(allFilters, buildDocumentSearchFilter(searchQuery, usePartialMatchSearch));
          const isCodeSearch = isCodeIndexQuery(searchQuery);

          // Фаза 1: SP + ES по названию — параллельно, быстро
          const [spResponse, esTitleResponse] = await Promise.all([
            page === 1
              ? getItemsRequest({
                  listName: myDocumentsListName,
                  filters: allFilters,
                  sortOrder: { name: "Created", desc: true },
                  rowLimit: 10,
                })
              : nextPage
                ? getItemsRequest({
                    listName: myDocumentsListName,
                    filters: allFilters,
                    sortOrder: { name: "Created", desc: true },
                    rowLimit: 10,
                    nextPage,
                  })
                : Promise.resolve({ items: [] as any[], nextPage: null }),
            isCodeSearch || !usePartialMatchSearch
              ? Promise.resolve({ results: [] as MyDocumentsType[], hasMore: false, lastFrom: 0 })
              : page === 1
                ? fetchElasticResultsWithFallback(elasticSearchByTitle, 0)
                : hasMoreEsTitle
                  ? fetchElasticResultsWithFallback(elasticSearchByTitle, esTitleFrom)
                  : Promise.resolve({ results: [] as MyDocumentsType[], hasMore: false, lastFrom: esTitleFrom }),
          ]);

          if (cancelled) return;

          const spResults = modified(modifiedItemsState(spResponse.items, myDocumentsListName));
          const phase1Docs = mergeSearchResults(spResults, esTitleResponse.results, []);

          setNextPage(spResponse.nextPage ?? null);
          setHasMoreEsTitle(esTitleResponse.hasMore);
          if (esTitleResponse.hasMore) setEsTitleFrom(esTitleResponse.lastFrom + 100);
          else if (page === 1) setEsTitleFrom(0);

          if (page === 1) {
            setDocuments(phase1Docs);
          } else {
            setDocuments((prev) => {
              const existingIds = new Set(prev.map((d) => d.id));
              return [...prev, ...phase1Docs.filter((d) => !existingIds.has(d.id))];
            });
          }

          // Фаза 2: ES по тексту документа — стримим чанками прямо в список
          // loading остаётся true до конца фазы 2, чтобы scroll-триггер не стрелял повторно
          if (!isCodeSearch && searchByContent && (page === 1 || hasMoreEsText)) {
            const startFrom = page === 1 ? 0 : esTextFrom;

            const TEXT_PAGE_SIZE = 25;
            const esTextResponse = await fetchElasticResultsWithFallback(
              (q, from) => elasticSearchByText(q, from, TEXT_PAGE_SIZE),
              startFrom,
              TEXT_PAGE_SIZE,
              TEXT_PAGE_SIZE,
              (chunk) => {
                if (cancelled) return;
                setDocuments((prev) => {
                  const existingIds = new Set(prev.map((d) => d.id));
                  return [...prev, ...chunk.filter((d) => !existingIds.has(d.id))];
                });
              },
            );

            if (cancelled) return;
            setHasMoreEsText(esTextResponse.hasMore);
            if (esTextResponse.hasMore) setEsTextFrom(esTextResponse.lastFrom + TEXT_PAGE_SIZE);
            else if (page === 1) setEsTextFrom(0);
          } else if (page === 1) {
            setHasMoreEsText(false);
            setEsTextFrom(0);
          }

          return;
        }

        if (page === 1) {
          const myDocsResponse = await getItemsRequest({
            listName: myDocumentsListName,
            filters: allFilters,
            sortOrder: { name: "Created", desc: true },
            rowLimit: 25,
          });

          if (cancelled) return;
          setNextPage(myDocsResponse.nextPage);

          const modifiedmyDocs = modifiedItemsState(myDocsResponse.items, myDocumentsListName);
          const results = modified(modifiedmyDocs);
          const tagged =
            searchQuery.length > 0
              ? results.map((d) => ({
                  ...d,
                  searchMeta: d.searchMeta ?? {
                    foundBy: "title" as const,
                    highlightedText: "",
                    elasticsearchId: "",
                    elasticsearchScore: 0,
                    DocumentFileId: null,
                  },
                }))
              : results;

          setDocuments(tagged);
        } else {
          const myDocsResponse = await getItemsRequest({
            listName: myDocumentsListName,
            filters: allFilters,
            sortOrder: { name: "Created", desc: true },
            rowLimit: 25,
            nextPage: nextPage,
          });

          if (cancelled) return;
          setNextPage(myDocsResponse.nextPage);

          const modifiedmyDocs = modifiedItemsState(myDocsResponse.items, myDocumentsListName);

          const results = modified(modifiedmyDocs);
          const tagged =
            searchQuery.length > 0
              ? results.map((d) => ({
                  ...d,
                  searchMeta: d.searchMeta ?? {
                    foundBy: "title" as const,
                    highlightedText: "",
                    elasticsearchId: "",
                    elasticsearchScore: 0,
                    DocumentFileId: null,
                  },
                }))
              : results;
          setDocuments((prev) => [...prev, ...tagged]);
        }
      } catch (error) {
        console.error("ошибка подгрузки документов");
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchData();
    return () => {
      cancelled = true;
    };
  }, [
    dispatch,
    activeFilterType,
    JSON.stringify(filters),
    JSON.stringify(folders),
    page,
    searchQuery,
    searchByPartialMatch,
    searchByContent,
    filtersReady,
  ]);

  useEffect(() => {
    if (documents.length) {
      const documentsResponsibleEmails = documents.map((document) => document.responsibleForRevision.email.trim());

      dispatch(addUsers(documentsResponsibleEmails));
    }
  }, [documents]);

  return (
    <>
      <FlexRow className="px-7.5 py-5 h-[76px] border-b items-center flex-none relative">
        {computedBreadcrumbs}
        {/* DocFavoriteFiltersManager must render in BOTH modes so that useDocDefaultFilters
            runs the Priority 2 chain (user's collection + department) even when the user
            opens the page directly in folder mode. The component itself returns null unless
            the bundles modal is open, so there is no visible impact in folder mode. */}
        <DocFavoriteFiltersManager
          isFavorite={isFavorite}
          setIsFavorite={setIsFavorite}
          currentFavoriteId={currentFavoriteId}
          setCurrentFavoriteId={setCurrentFavoriteId}
          bundlesModalOpened={bundlesModalOpened && activeFilterType === "filter"}
          setBundlesModalOpened={setBundlesModalOpened}
          setFiltersReady={setFiltersReady}
        />
      </FlexRow>
      <FlexRow className="pl-[30px] pr-[26px] pt-6">
        <FlexCol className="w-full">
          <FlexRow className="mb-2 gap-x-2.5">
            <FlexCol className="grow">
              <SearchUI
                placeholder={
                  searchByPartialMatch || searchByContent
                    ? "Найти документ по частичному совпадению названия или кода документа"
                    : "Найти документ по названию или коду документа"
                }
                defaultValue={searchQuery}
                onClick={handleSearch}
              />
              <FlexRow className="mb-[12px] mt-[5px] gap-x-5 items-center">
                <label className="flex gap-x-2 items-center cursor-pointer select-none">
                  <CustomCheckbox checked={searchByPartialMatch} onChange={handleToggleSearchByPartialMatch}>
                    <span className="text-sm text-[#637381]">Искать по частичному совпадению</span>
                  </CustomCheckbox>
                </label>
                <label className="flex gap-x-2 items-center cursor-pointer select-none">
                  <CustomCheckbox checked={searchByContent} onChange={handleToggleSearchByContent}>
                    <span className="text-sm text-[#637381]">Искать по тексту документа</span>
                  </CustomCheckbox>
                </label>
              </FlexRow>
            </FlexCol>
            <FlexCol className="shrink-0">
              <DocumentRegister />
            </FlexCol>
            <FlexCol className="shrink-0">
              <AddCard />
              {searchQuery.length > 0 && (
                <span className="mt-[5px] text-sm text-right whitespace-nowrap text-[#637381]">
                  Найдено документов: {documents.length}
                </span>
              )}
            </FlexCol>
          </FlexRow>

          <TableWithFilters documents={documents} cb={cb} loading={loading} hasNextPage={!!nextPage || hasMoreEsTitle || hasMoreEsText} />
        </FlexCol>
      </FlexRow>
      {createPortal(
        <FavoriteFiltersModal
          closeModal={() => setFavoriteFilterModalOpened(false)}
          opened={favoriteFilterModalOpened}
          saveFilter={saveFilter}
        />,
        document.body,
      )}
      <DialogComponent open={isSaved} toggleDialog={closeDialog}>
        <FlexCol className="w-[600px] shrink">
          <FlexRow className="mb-9">
            <h3 className="text-black">Назовите набор фильтров</h3>
          </FlexRow>
          <FlexRow className="mb-9">
            <InputFilterSet initial={filterTitle} onChange={getSetTitle} />
          </FlexRow>
          <FlexRow className="justify-between px-10 pt-10 -mx-10 border-t">
            <FlexCol>
              <TGButton onClick={closeDialog}>Отмена</TGButton>
            </FlexCol>
            <FlexCol>
              <TGButton color="bluedark" icon={<SaveIcon />} onClick={updateSetFilter}>
                Сохранить
              </TGButton>
            </FlexCol>
          </FlexRow>
        </FlexCol>
      </DialogComponent>
    </>
  );
}

export default MyDocumentPage;

function DocumentRegister() {
  return (
    <>
      <a
        className="flex items-center py-3 px-6 rounded-md leading-[22px] disabled:bg-[#E5E7EB] disabled:text-[#374151] disabled:cursor-not-allowed bg-white shadow-custom text-darkblue"
        href={`${siteUrl}/dashboard/1/reports/1`}
      >
        Реестр документов
      </a>
    </>
  );
}
